export interface ImpersonationSession {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    name?: string | null;
  };
  organization: {
    id: string;
    name: string;
    role: string;
    isPersonal: boolean;
  };
  impersonated: {
    adminUsername: string;
    startedAt: string;
  };
}

const IMPERSONATION_SESSION_KEY = "impersonationSession";

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

export function getImpersonationSession(): ImpersonationSession | null {
  if (!hasWindow()) {
    return null;
  }

  const raw = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as ImpersonationSession;
  } catch (error) {
    console.error("Failed to parse impersonation session:", error);
    sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
    return null;
  }
}

export function isImpersonating(): boolean {
  return getImpersonationSession() !== null;
}

export function startImpersonationSession(session: ImpersonationSession): void {
  if (!hasWindow()) {
    return;
  }

  sessionStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(session));
  sessionStorage.setItem("currentOrganizationId", session.organization.id);
  sessionStorage.removeItem("defaultOrganizationId");
  sessionStorage.removeItem("showOrgSwitcherTooltip");
}

export function updateImpersonationTokens(accessToken: string, refreshToken: string): void {
  const session = getImpersonationSession();
  if (!session) {
    return;
  }

  startImpersonationSession({
    ...session,
    accessToken,
    refreshToken,
  });
}

export function clearImpersonationSession(): void {
  if (!hasWindow()) {
    return;
  }

  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
  sessionStorage.removeItem("currentOrganizationId");
  sessionStorage.removeItem("defaultOrganizationId");
  sessionStorage.removeItem("showOrgSwitcherTooltip");
}

export function getScopedStorageItem(key: string): string | null {
  if (!hasWindow()) {
    return null;
  }

  if (isImpersonating()) {
    return sessionStorage.getItem(key);
  }

  return localStorage.getItem(key);
}

export function setScopedStorageItem(key: string, value: string): void {
  if (!hasWindow()) {
    return;
  }

  if (isImpersonating()) {
    sessionStorage.setItem(key, value);
    return;
  }

  localStorage.setItem(key, value);
}

export function removeScopedStorageItem(key: string): void {
  if (!hasWindow()) {
    return;
  }

  if (isImpersonating()) {
    sessionStorage.removeItem(key);
    return;
  }

  localStorage.removeItem(key);
}
